/* The picture library: what is on the shelf, and in what order.
 *
 * Twelve pictures somebody can caption with one click instead of going and
 * finding a photograph of their own. Every one was generated rather than
 * photographed, and none is a copy of an existing image -- which is the whole
 * basis on which a shelf chosen by the operator can exist at all. Anything the
 * operator picks and ships is the operator's own act, outside the DMCA safe
 * harbour for material stored at a user's direction, so there is no procedural
 * cushion behind these and provenance is recorded per file rather than in
 * general. library-manifest.json holds that record; tools/make_library.py
 * writes it; tests/test_library.py checks the two agree.
 *
 * Compare lib/starters.js, which is the other half of the same idea and needs
 * none of the above: those are a few lines of canvas the browser runs, so
 * nothing is served and there is nothing to record.
 *
 * WHY THE LIST IS HERE AND THE FILES ARE NOT. Three sizes of each picture live
 * in public/library/ and are named by convention off the key -- <key>.jpg,
 * <key>-thumb.jpg, <key>-thumb2x.jpg. Restating those names here would be
 * three more chances to mistype one, and a missing thumbnail is a broken image
 * in a row of twelve rather than an error anybody sees in a log.
 *
 * THE DESCRIPTIONS ARE IN ENGLISH, deliberately and unlike everything else the
 * interface says. They describe a specific picture, so they belong beside the
 * list of pictures rather than in a catalogue keyed by ten languages -- the same
 * reasoning tutorials.json is written under. Only English is published today
 * (see "One published language" in CLAUDE.md); if that changes, these twelve
 * sentences want translating and this comment is the reminder.
 */

export const LIBRARY_PATH = "/library/"

/* Ordered for a grid rather than alphabetically: the two-panel formats sit
 * together at the front because they are the ones somebody arrives wanting,
 * and they read oddly interleaved with the single scenes.
 *
 * `name` is the short title. It shows on the thumbnail, and it is what
 * tools/make_task_pages.py puts in the ImageObject on the page -- the answer to
 * "how does a search engine learn what these pictures are of" is that the page
 * has to say so in words, because a filename and a thumbnail say nothing.
 *
 * `alt` is for somebody who cannot see the picture.
 *
 * `areas` is where the picture's own caption spaces are, when it has any, and
 * they are MEASURED rather than guessed: `make_library.py --areas` scans for
 * near-white rectangles and prints exactly these numbers. Three of the twelve
 * have them; the other nine get the default bands. Fractions of the picture, and
 * anchor "centre" because a box painted into artwork is drawn tight around the
 * space and the words belong in the middle of it -- usableMemeBox() in
 * lib/meme.js says more about why the anchor travels with the box.
 */
export const LIBRARY = [
	{key: "exchanging-glances",
		name: "Looking back",
		alt: "A man walking arm in arm with one woman turns to look back at another"},
	{key: "a-or-b",
		name: "Two buttons",
		alt: "Two large red buttons marked Option A and Option B, and below them a man sweating over the choice"},
	{key: "yes-no",
		name: "No, then yes",
		alt: "The same man twice: waving something away in the first panel, pointing approvingly at it in the second",
		// The two white bars the artwork leaves under each panel. Side by side
		// at the same height, which is exactly why the anchor matters.
		areas: [
			{x: 0.031, y: 0.727, w: 0.438, h: 0.227, anchor: "centre"},
			{x: 0.531, y: 0.727, w: 0.438, h: 0.227, anchor: "centre"},
		]},
	{key: "happy-and-frowning",
		name: "Glad, then grim",
		alt: "The same man twice in black and white: beaming at the camera in the first panel, frowning at it in the second",
		// Two white bars again, side by side, the same shape as yes-no.
		areas: [
			{x: 0.016, y: 0.750, w: 0.469, h: 0.227, anchor: "centre"},
			{x: 0.516, y: 0.750, w: 0.469, h: 0.227, anchor: "centre"},
		]},
	{key: "angry-lady-cat2",
		name: "Shouting at a cat",
		alt: "A woman shouting and pointing, beside a cat sitting at a table behind a bowl of salad",
		// One white band across the top. Area 2 falls back to the default
		// bottom band, which is where it wants to be anyway.
		areas: [
			{x: 0.02, y: 0.012, w: 0.96, h: 0.20, anchor: "centre"},
		]},
	{key: "serious-doctor",
		name: "The grave diagnosis",
		alt: "A doctor looking gravely across a desk at a patient seen from behind"},
	{key: "was-naughty",
		name: "Standing by the fire",
		alt: "A child smiling at the camera while a house burns behind her and firefighters work at it"},
	{key: "last-second-exit",
		name: "The last-second exit",
		alt: "A car swerving across empty motorway lanes to take an exit at the last moment"},
	{key: "oompf",
		name: "Fighting the sea",
		alt: "A small child on a beach punching a fistful of sand, waves breaking behind"},
	{key: "bewilderment",
		name: "Something in the sky",
		alt: "A bearded man lit blue from below, staring upward with his mouth open"},
	{key: "think-about-it",
		name: "Tapping his temple",
		alt: "A young man in a leather jacket smiling and pointing a finger at his own temple"},
	{key: "take-my-money",
		name: "Take my money",
		alt: "A cartoon man in a red jacket grinning and holding out a fan of hundred-dollar bills"},
]

export const LIBRARY_KEYS = LIBRARY.map((p) => p.key)

export function thumbFor(key) {
	return LIBRARY_PATH + key + "-thumb.jpg"
}

export function thumbFor2x(key) {
	return LIBRARY_PATH + key + "-thumb2x.jpg"
}

export function fullFor(key) {
	return LIBRARY_PATH + key + ".jpg"
}

/* Where this picture's own caption spaces are, if it declared any. The first
 * is Area 1, the second Area 2; a picture that declares one gets the default
 * band for the other, and a picture that declares none gets both defaults. */
export function areasFor(key) {
	const found = LIBRARY.find((p) => p.key === key)
	return (found && found.areas) || []
}

/* Fetch one and hand back a File, so a library picture enters the application
 * through exactly the same door as a dropped photograph.
 *
 * A File rather than a URL for the reason lib/starters.js returns one:
 * addFiles() already knows how to accept, order, refuse and preview one, and a
 * second intake path would be a second place for every rule about intake to be
 * forgotten. Same-origin, so the canvas that captions it is never tainted --
 * which is what would otherwise make the download throw at the last moment.
 */
export async function libraryFile(key) {
	if (!LIBRARY_KEYS.includes(key)) return null
	try {
		const res = await fetch(fullFor(key), {cache: "force-cache"})
		if (!res.ok) return null
		const blob = await res.blob()
		if (!blob || !blob.size) return null
		return new File([blob], key + ".jpg", {type: blob.type || "image/jpeg"})
	} catch (e) {
		// A shelf that cannot be reached is a shelf nobody can use, and the drop
		// zone beside it still works. Nothing to say out loud.
		return null
	}
}
